import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { Project } from '../hooks/useProjects';
import {
  useProjectChangelog,
  computeDiff,
  isLinkTable,
  getLinkTableInfo,
  formatLinkOp,
  buildEntityNameMap,
  type ChangelogEntry,
  type NameMap,
} from '../hooks/useProjectChangelog';
import { CT } from '../lib/theme';

const SIDEBAR_W = 360;

// Entity table that corresponds to a link table's foreign key
const LINK_ENTITY_MAP: Record<string, { table: string; key: string }[]> = {
  link_persona_situation: [
    { table: 'persona', key: 'persona_id' },
    { table: 'situation', key: 'situation_id' },
  ],
  link_persona_situation_pain: [
    { table: 'pain', key: 'pain_id' },
  ],
  link_solution_pain: [
    { table: 'solution', key: 'solution_id' },
    { table: 'pain', key: 'pain_id' },
  ],
  link_solution_journey: [
    { table: 'solution', key: 'solution_id' },
    { table: 'journey', key: 'journey_id' },
  ],
};

/**
 * Filter out link-table DELETE entries when the referenced entity
 * was also deleted — those are redundant cascade noise.
 */
function filterRedundantLinkDeletes(entries: ChangelogEntry[]): ChangelogEntry[] {
  // Collect all entity DELETEs: "persona:42" etc.
  const entityDeletes = new Set<string>();
  for (const e of entries) {
    if (e.op === 'DELETE' && !isLinkTable(e.tableName)) {
      entityDeletes.add(`${e.tableName}:${e.recordId}`);
    }
  }
  if (entityDeletes.size === 0) return entries;

  return entries.filter((e) => {
    if (e.op !== 'DELETE' || !isLinkTable(e.tableName)) return true;
    const mappings = LINK_ENTITY_MAP[e.tableName];
    if (!mappings) return true;
    const data = e.oldRecord ?? e.record ?? {};
    return !mappings.some((m) => entityDeletes.has(`${m.table}:${data[m.key]}`));
  });
}

const TYPE_COLORS: Record<string, string> = {
  persona: CT.orange,
  situation: CT.purple,
  pain: CT.blue,
  solution: CT.green,
  project: CT.white,
  journey: CT.white,
  journey_step: CT.paleGrey,
  link_persona_situation: CT.paleGrey,
  link_persona_situation_pain: CT.paleGrey,
  link_solution_pain: CT.paleGrey,
  link_solution_journey: CT.paleGrey,
};

const OP_LABELS: Record<string, { label: string; color: string }> = {
  INSERT: { label: 'Created', color: CT.green },
  UPDATE: { label: 'Updated', color: CT.blue },
  DELETE: { label: 'Deleted', color: '#f87171' },
};

function relativeTime(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function shortEmail(email: string | null | undefined): string {
  if (!email) return 'system';
  const at = email.indexOf('@');
  return at > 0 ? email.substring(0, at) : email;
}

function ChangelogItem({ entry, nameMap, onFocusNode }: { entry: ChangelogEntry; nameMap: NameMap; onFocusNode?: (nodeId: string) => boolean }) {
  const [expanded, setExpanded] = useState(false);
  const opInfo = OP_LABELS[entry.op] ?? { label: entry.op, color: CT.paleGrey };
  const dotColor = TYPE_COLORS[entry.entityType] ?? CT.paleGrey;
  const isLink = isLinkTable(entry.tableName);

  const diffs = useMemo(
    () => (entry.op === 'UPDATE' ? computeDiff(entry.record, entry.oldRecord) : []),
    [entry.op, entry.record, entry.oldRecord]
  );
  const hasDiff = diffs.length > 0 || isLink;

  const linkFormatted = isLink ? formatLinkOp(entry, nameMap) : null;
  const displayName = linkFormatted?.text ?? entry.entityName ?? entry.tableName;
  const hoverTitle = linkFormatted?.title ?? displayName;

  const typeLabel = isLink
    ? ''
    : entry.entityType.charAt(0).toUpperCase() + entry.entityType.slice(1);

  // Can we focus this entity on the canvas?
  let focusNodeId: string | null = null;
  if (onFocusNode && entry.op !== 'DELETE') {
    if (!isLink) {
      const prefix = ENTITY_NODE_PREFIX[entry.entityType];
      if (prefix) focusNodeId = `${prefix}${entry.recordId}`;
    } else {
      // For link entries, focus on the "to" entity (e.g. pain for link_solution_pain)
      const linkInfo = getLinkTableInfo(entry.tableName);
      if (linkInfo) {
        const data = entry.record ?? entry.oldRecord ?? {};
        const toId = data[linkInfo.toKey];
        const toPrefix = ENTITY_NODE_PREFIX[linkInfo.to];
        if (toId != null && toPrefix) focusNodeId = `${toPrefix}${toId}`;
      }
    }
  }
  const canFocus = focusNodeId != null;
  const [toast, setToast] = useState<string | null>(null);
  const handleClick = canFocus
    ? () => {
        const found = onFocusNode!(focusNodeId!);
        if (!found) {
          setToast('No longer on the map');
          setTimeout(() => setToast(null), 2000);
        }
      }
    : undefined;

  return (
    <div
      style={{
        padding: '10px 16px',
        borderBottom: `1px solid ${CT.lightGrey}20`,
      }}
    >
      <div
        onClick={handleClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 4,
          cursor: canFocus ? 'pointer' : 'default',
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: dotColor,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            color: CT.white,
            fontSize: 12,
            fontWeight: 500,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textDecoration: canFocus ? 'underline' : 'none',
            textDecorationColor: canFocus ? `${CT.white}40` : undefined,
            textUnderlineOffset: 2,
          }}
          title={hoverTitle}
        >
          {typeLabel ? `${typeLabel}: ` : ''}{displayName}
        </span>
      </div>

      {toast && (
        <div style={{ paddingLeft: 14, fontSize: 11, color: '#f59e0b', marginBottom: 2 }}>
          {toast}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          color: CT.paleGrey,
          paddingLeft: 14,
        }}
      >
        <span style={{ color: opInfo.color, fontWeight: 500 }}>{opInfo.label}</span>
        <span>·</span>
        <span>{relativeTime(entry.ts)}</span>
        <span>·</span>
        <span title={entry.authEmail ?? undefined}>{shortEmail(entry.authEmail)}</span>
      </div>

      {hasDiff && entry.op !== 'INSERT' && entry.op !== 'DELETE' && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            background: 'none',
            border: 'none',
            color: CT.primaryBlue,
            fontSize: 11,
            cursor: 'pointer',
            padding: '4px 0 0 14px',
            fontFamily: CT.font,
          }}
        >
          {expanded ? '▾ Hide changes' : '▸ Show changes'}
        </button>
      )}

      {expanded && diffs.length > 0 && (
        <div style={{ paddingLeft: 14, paddingTop: 4 }}>
          {diffs.map((d) => (
            <div key={d.field} style={{ fontSize: 11, lineHeight: 1.5 }}>
              <span style={{ color: CT.paleGrey }}>{d.field}: </span>
              {d.from != null && (
                <span style={{ color: '#f87171', textDecoration: 'line-through', opacity: 0.7 }}>
                  {String(d.from)}
                </span>
              )}
              {d.from != null && d.to != null && <span style={{ color: CT.paleGrey }}> → </span>}
              {d.to != null && (
                <span style={{ color: CT.green }}>{String(d.to)}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectList({
  projects,
  selectedId,
  onSelect,
  isLoading,
  error,
}: {
  projects: Project[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  isLoading: boolean;
  error?: Error | null;
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    if (!query) return projects;
    const q = query.toLowerCase();
    return projects.filter((p) => p.name.toLowerCase().includes(q));
  }, [projects, query]);

  if (isLoading) {
    return <div style={{ padding: 16, color: CT.paleGrey, fontSize: 13 }}>Loading projects…</div>;
  }
  if (error) {
    return <div style={{ padding: 16, color: '#f87171', fontSize: 13 }}>Error loading projects</div>;
  }

  return (
    <div>
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && filtered.length === 1) {
            onSelect(filtered[0].id);
          }
        }}
        placeholder="Search projects…"
        style={{
          width: '100%',
          padding: '10px 16px',
          border: 'none',
          borderBottom: `1px solid ${CT.lightGrey}20`,
          background: 'transparent',
          color: CT.white,
          fontSize: 13,
          fontFamily: CT.font,
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
      <div style={{ maxHeight: 300, overflowY: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '12px 16px', color: CT.paleGrey, fontSize: 13 }}>No matches</div>
        ) : (
          filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => onSelect(p.id)}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 16px',
                border: 'none',
                background: selectedId === p.id ? 'rgba(2, 132, 254, 0.15)' : 'transparent',
                color: selectedId === p.id ? CT.white : CT.paleGrey,
                fontSize: 13,
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: CT.font,
              }}
            >
              {p.name}
            </button>
          ))
        )}
      </div>
    </div>
  );
}

const ENTITY_NODE_PREFIX: Record<string, string> = {
  persona: 'per',
  situation: 'sit',
  pain: 'pain',
  solution: 'sol',
};

export type ViewMode = 'research' | 'development';

function ModeSwitcher({
  mode,
  onModeChange,
  hasSolutions,
}: {
  mode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  hasSolutions: boolean;
}) {
  const btnStyle = (active: boolean, disabled?: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '5px 0',
    border: 'none',
    borderRadius: 4,
    background: active ? CT.primaryBlue : 'transparent',
    color: active ? CT.white : disabled ? `${CT.paleGrey}50` : CT.paleGrey,
    fontSize: 11,
    fontWeight: 500,
    fontFamily: CT.font,
    cursor: disabled ? 'default' : 'pointer',
    transition: 'background 0.15s, color 0.15s',
  });

  return (
    <div
      style={{
        display: 'flex',
        gap: 2,
        padding: '0 16px 10px',
        background: `${CT.darkBg}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 2,
          background: `${CT.lightGrey}20`,
          borderRadius: 6,
          padding: 2,
          width: '100%',
        }}
      >
        <button
          onClick={() => onModeChange('research')}
          title="Persona → Situation → Pain → Solution"
          style={btnStyle(mode === 'research')}
        >
          Research
        </button>
        <button
          onClick={() => hasSolutions && onModeChange('development')}
          disabled={!hasSolutions}
          title={hasSolutions ? 'Solution → Deliverable → PRD' : 'No solutions in this project yet'}
          style={btnStyle(mode === 'development', !hasSolutions)}
        >
          Development
        </button>
      </div>
    </div>
  );
}

export default function Sidebar({
  projects,
  selectedId,
  onSelect,
  isLoading,
  error,
  selectedProjectName,
  autoOpen,
  onCopyShareLink,
  copied,
  onFocusNode,
  mode,
  onModeChange,
  hasSolutions,
  showModeSwitcher,
}: {
  projects: Project[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  isLoading: boolean;
  error?: Error | null;
  selectedProjectName?: string;
  autoOpen?: boolean;
  onCopyShareLink?: () => void;
  copied?: boolean;
  onFocusNode?: (nodeId: string) => boolean;
  mode?: ViewMode;
  onModeChange?: (mode: ViewMode) => void;
  hasSolutions?: boolean;
  showModeSwitcher?: boolean;
}) {
  const [open, setOpen] = useState(autoOpen ?? selectedId == null);
  const [showProjectList, setShowProjectList] = useState(selectedId == null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  const { entries: rawEntries, isLoading: changelogLoading, hasMore, loadMore, refresh } =
    useProjectChangelog(selectedId);

  const entries = useMemo(
    () => filterRedundantLinkDeletes(rawEntries).filter((e) => e.authUid),
    [rawEntries]
  );
  const nameMap = useMemo(() => buildEntityNameMap(rawEntries), [rawEntries]);

  // Auto-open when no project selected
  useEffect(() => {
    if (selectedId == null) {
      setOpen(true);
      setShowProjectList(true);
    }
  }, [selectedId]);

  const handleProjectSelect = useCallback(
    (id: number) => {
      onSelect(id);
      setShowProjectList(false);
    },
    [onSelect]
  );

  const handleScrollEnd = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 100 && hasMore) {
        loadMore();
      }
    },
    [hasMore, loadMore]
  );

  return (
    <>
      {/* Hamburger / toggle button — always visible */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{
            position: 'absolute',
            top: 16,
            left: 16,
            zIndex: 10,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontFamily: CT.font,
          }}
        >
          <svg width="18" height="14" viewBox="0 0 18 14" fill="none">
            <path d="M1 1h16M1 7h16M1 13h16" stroke={CT.paleGrey} strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          {selectedProjectName && (
            <span style={{ color: CT.white, fontSize: 16, fontWeight: 600 }}>
              {selectedProjectName}
            </span>
          )}
        </button>
      )}

      {/* Sidebar panel */}
      <div
        ref={sidebarRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: SIDEBAR_W,
          background: CT.darkBg,
          borderRight: `1px solid ${CT.lightGrey}20`,
          zIndex: 20,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: CT.font,
          transform: open ? 'translateX(0)' : `translateX(-${SIDEBAR_W}px)`,
          transition: 'transform 0.25s ease',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${CT.lightGrey}20`,
            gap: 8,
            minHeight: 48,
          }}
        >
          {selectedId != null && !showProjectList ? (
            <>
              <button
                onClick={() => setShowProjectList(true)}
                title="Switch project"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  color: CT.paleGrey,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M2 4h12M2 8h12M2 12h12" />
                </svg>
              </button>
              <span
                style={{
                  color: CT.white,
                  fontSize: 15,
                  fontWeight: 600,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedProjectName ?? 'Project'}
              </span>
              {onCopyShareLink && (
                <button
                  onClick={onCopyShareLink}
                  title="Copy share link"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    color: copied ? CT.green : CT.lightGrey,
                    fontSize: 12,
                    fontFamily: CT.font,
                    transition: 'color 0.2s',
                  }}
                >
                  {copied ? 'Copied!' : (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6.5 8.5a3 3 0 0 0 4.2.4l2-2a3 3 0 0 0-4.2-4.2L7.3 3.9" />
                      <path d="M9.5 7.5a3 3 0 0 0-4.2-.4l-2 2a3 3 0 0 0 4.2 4.2l1.2-1.2" />
                    </svg>
                  )}
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  color: CT.paleGrey,
                  fontSize: 16,
                }}
              >
                ✕
              </button>
            </>
          ) : (
            <>
              <span style={{ color: CT.white, fontSize: 14, fontWeight: 600, flex: 1 }}>
                Projects
              </span>
              {selectedId != null && (
                <button
                  onClick={() => setShowProjectList(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 4,
                    color: CT.paleGrey,
                    fontSize: 16,
                  }}
                >
                  ✕
                </button>
              )}
            </>
          )}
        </div>

        {/* Mode switcher (only for authorized users) */}
        {showModeSwitcher && mode && onModeChange && selectedId != null && !showProjectList && (
          <ModeSwitcher
            mode={mode}
            onModeChange={onModeChange}
            hasSolutions={hasSolutions ?? false}
          />
        )}

        {/* Content */}
        {showProjectList ? (
          <ProjectList
            projects={projects}
            selectedId={selectedId}
            onSelect={handleProjectSelect}
            isLoading={isLoading}
            error={error}
          />
        ) : (
          <div
            onScroll={handleScrollEnd}
            style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}
          >
            {/* Changelog header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 16px',
                borderBottom: `1px solid ${CT.lightGrey}20`,
              }}
            >
              <span style={{ color: CT.paleGrey, fontSize: 12, fontWeight: 500, flex: 1 }}>
                Recent changes
              </span>
              <button
                onClick={refresh}
                title="Refresh"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 2,
                  color: CT.lightGrey,
                  fontSize: 12,
                }}
              >
                ↻
              </button>
            </div>

            {changelogLoading && entries.length === 0 ? (
              <div style={{ padding: 16, color: CT.paleGrey, fontSize: 12 }}>Loading…</div>
            ) : entries.length === 0 ? (
              <div style={{ padding: 16, color: CT.paleGrey, fontSize: 12 }}>
                No changes recorded yet
              </div>
            ) : (
              <>
                {entries.map((e) => (
                  <ChangelogItem key={e.id} entry={e} nameMap={nameMap} onFocusNode={onFocusNode} />
                ))}
                {changelogLoading && (
                  <div style={{ padding: 12, color: CT.paleGrey, fontSize: 11, textAlign: 'center' }}>
                    Loading more…
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Backdrop when sidebar is open (click to close) */}
      {open && selectedId != null && (
        <div
          onClick={() => {
            setOpen(false);
            setShowProjectList(false);
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: SIDEBAR_W,
            right: 0,
            bottom: 0,
            zIndex: 15,
            background: 'rgba(0,0,0,0.3)',
            transition: 'opacity 0.25s ease',
          }}
        />
      )}
    </>
  );
}
